<template>
    <div v-if="!isEnabled">
        <al-message type="info">
            <div>
                <p><strong>This feature works best with shorter date ranges.</strong></p>
                <p>Select <strong>less than 2 weeks</strong> to enable it.</p>
            </div>
        </al-message>
    </div>
    <section class="grid-container-wrapper" v-if="isEnabled">
        <div class="grid-container explorer">
            <div class="explorer-controls">
                <!-- TODO: Make these almendra controls -->
                <div class="labelled-control">
                    <label for="backlog-explorer-select-status">State</label>
                    <select id="backlog-explorer-select-status" v-model="selectedStatus">
                        <option value="Open">Open</option>
                        <option value="Created">Created</option>
                        <option value="Closed">Closed</option>
                    </select>
                </div>
                <!-- The Available Dates field is dynamically populated based on the selected Status. To ensure a logical user
                flow and maintain data dependency, the date field selection is positioned below the status dropdown-->
                <div v-if="rowCount > 0" class="labelled-control">
                    <label for="backlog-explorer-select-snapshot">Snapshot Date</label>
                    <select id="backlog-explorer-select-snapshot" v-model="selectedDate">
                        <option selected :value="undefined">--- Please select a snapshot ---</option>
                        <option v-for="item in availableDates" :key="item.getTime()" :value="item">
                            {{ format(item, 'PP') }}
                        </option>
                    </select>
                </div>

                <div v-if="rowCount > 0" class="explorer-export-wrapper">
                    <al-button class="explorer-export" size="small" variant="primary" :disabled="!selectedDate || exportLoading" @click="onExport">
                        <al-icon icon="export"></al-icon>
                        Export
                        <al-loader v-if="exportLoading" size="small"></al-loader>
                    </al-button>
                </div>
            </div>

            <!-- Display empty message if no matching records -->
            <al-message v-if="rowCount === 0" type="info">
                Advanced data collection is not available for this selected period and status.
            </al-message>

            <ag-grid-vue
                v-else
                ref="gridRef"
                class="ag-theme-alpine grid"
                :column-defs="columnDefs"
                :row-data="rowData"
                :dom-layout="domLayout"
                :modules="modules"
                suppress-set-filter-by-default="true"
                role="table"
                aria-label="Backlog Explorer data table"
                :auto-size-strategy="autoSizeStrategy"
                :pagination="true"
                :pagination-page-size="20"
                :pagination-page-size-selector="[20, 50, 100]"
                :floating-filter="true"
                :loading="isLoading"
                :error="error"
                @row-data-updated="onGridRowDataUpdated"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { ref, toRef, watch } from 'vue';
import { differenceInDays, format } from 'date-fns';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { Module, RowDataUpdatedEvent } from '@ag-grid-community/core';
import { useExplorerData } from '@/pages/OperationsInsights/composables/useExplorerData';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter';
import { useExplorerExport } from '@/pages/OperationsInsights/composables/useExplorerExport';

const MAX_EXPLORER_DAYS = 365;

type Props = {
    queryParams: ApiQueryParams;
};
const props = defineProps<Props>();
const isEnabled = defineModel<boolean>('isEnabled');

const logger = useLogger('BacklogInsightsTab');
const queryParamsRef = toRef(props, 'queryParams');

const selectedDate = ref<Date | undefined>();
const selectedStatus = ref<string>('Open');

watch(
    queryParamsRef,
    (newParams) => {
        logger.debug('queryParams changed', newParams);
        const filterDays = differenceInDays(newParams.toDate, newParams.fromDate);
        isEnabled.value = 0 < filterDays && filterDays <= MAX_EXPLORER_DAYS;
    },
    { deep: true, immediate: true }
);

const gridRef = ref();
const modules = [ClientSideRowModelModule, SetFilterModule, MultiFilterModule] as Module[];
const domLayout = 'autoHeight';
const autoSizeStrategy = { type: 'fitGridWidth', defaultMinWidth: 100 };

const { columnDefs, rowData, rowCount, availableDates, isLoading, error } = useExplorerData(queryParamsRef, selectedStatus, selectedDate);
const { fetchExport, exportUrl, isLoading: exportLoading, error: _exportError } = useExplorerExport(queryParamsRef, selectedStatus, selectedDate);

const onExport = async () => {
    if (!exportUrl.value?.url) {
        logger.debug('Fetching export URL');
        await fetchExport();
    } else {
        // Reuse existing valid link
        logger.debug('Reusing export URL');
    }
    if (exportUrl.value?.url) {
        const link = document.createElement('a');
        link.href = exportUrl.value.url;
        document.body.append(link);
        link.click();
        link.remove();
    }
};

const onGridRowDataUpdated = (ev: RowDataUpdatedEvent) => {
    ev.api.sizeColumnsToFit(autoSizeStrategy);
};
</script>

<style scoped lang="scss">
@use '../styles/backlog-insights-layout.scss';

.grid-container.explorer {
    flex-direction: column;

    .explorer-controls {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        justify-content: flex-start;
        width: 100%;
        gap: 0;
        margin: 0.5rem 0;

        .labelled-control {
            display: flex;
            flex-direction: row;
            align-items: baseline;
            align-content: center;
            justify-content: flex-start;
            gap: 0.2rem;
            padding: 0 1rem;

            label,
            select {
                width: auto;
            }
        }

        .explorer-export-wrapper {
            flex: 1;
            display: flex;
            width: 100%;
            justify-content: flex-end;
            justify-self: flex-end;
            align-self: center;
            align-items: center;
            align-content: center;

            .explorer-export {
                margin-right: 0.7rem;
                width: auto;
            }
        }
    }
}
</style>
